<template>
    <div>
        <div
            v-for="(field, keyName, index) in this.formFields"
            :key="field.name"
            :class="field.wrapperClass">
            <!-- hidden -->
            <InputElm
                v-if="field.type === 'hidden'"
                :inputConfig="field.config"
                @input="handleInput($event, field.name)"></InputElm>

            <LabelInputSheerID
                v-else-if="field.config.name === 'university'"
                :inputConfig="field.config"
                :errorMessage="field.errorMessage"
                @input="handleSheerIDInput($event)"
                @blur="handleBlur(null, field.name)"
                @keyup="handleKeyUp"></LabelInputSheerID>

            <!-- birth date -->
            <LabelInputBirthDate
                v-else-if="
                    field.type === 'birthdate' ||
                    field.type === 'date' ||
                    field.config.fieldType === 'date'
                "
                :inputConfig="field.config"
                :errorMessage="field.errorMessage"
                @keyup="handleKeyUp"
                @input="handleInput($event, field.name)"
                @blur="handleBlur(null, field.name)"></LabelInputBirthDate>

            <!-- text, email, password -->
            <LabelInput
                v-else-if="
                    field.type === 'text' || field.type === 'email' || field.type === 'password'
                "
                :inputConfig="field.config"
                :errorMessage="field.errorMessage"
                :elementIndex="index"
                :disableAutofocus="isFTC"
                @keyup="handleKeyUp"
                @input="handleInput($event, field.name)"
                @blur="handleBlur($event, field.name)"
                @showHidePasswordStatus="relayShowHidePasswordStatus"></LabelInput>

            <!-- checkbox -->
            <CheckboxElm
                v-else-if="field.type === 'checkbox'"
                :checkboxConfig="field.config"
                :errorMessage="field.errorMessage"
                @change="handleInput($event, field.name)"
                @trackCheckBox="handleTrackCheckBox($event, field.name)"></CheckboxElm>

            <!-- custom select -->
            <CustomSelect
                v-else-if="field.type === 'select'"
                :selectConfig="field.config"
                :errorMessage="field.errorMessage"
                @input="handleInput($event, field.name)"
                @blur="handleBlur($event, field.name)"></CustomSelect>

            <!-- START AB TEST PLOCTOPLUS-2033 : Added @focus -->
            <RecurlyHostedField
                v-else-if="field.type === 'recurly-hosted'"
                :inputConfig="field.config"
                :errorMessage="field.errorMessage"
                @focus="handleFocus"
                @input="handleInput($event, field.name)"
                @blur="handleBlur($event, field.name)"></RecurlyHostedField>
            <!-- END AB TEST PLOCTOPLUS-2033 : Added @focus -->

            <div
                :id="field.config.name + '_error-msg'"
                role="alert"
                aria-live="polite"
                class="text-input-error"
                v-if="field.errorMessage">
                {{ field.errorMessage }}
            </div>
            <!-- BEGIN PLOCTOPLUS-2033 -->
            <div v-if="shouldDisplayGreenLockIcon(field)" class="cc-lock-info">
                Your payment is secure with SSL encryption.
            </div>
            <div v-else-if="shouldDisplayHyperlinkText(field)" class="cc-hyperlink">
                <a
                    href="javascript:void(0)"
                    id="lock-icon-hyperlink"
                    @click="executeLockIconTracking()">
                    Learn more about how we keep your info safe.
                </a>
            </div>
            <!-- END PLOCTOPLUS-2033 -->
        </div>
    </div>
</template>

<script>
    import CheckboxElm from 'atoms/CheckboxElm';
    import CustomSelect from 'atoms/CustomSelect';
    import InputElm from 'atoms/InputElm';
    import LabelInput from 'atoms/LabelInput';
    import LabelInputBirthDate from 'atoms/LabelInputBirthDate';
    import LabelInputSheerID from 'atoms/LabelInputSheerID';
    import RecurlyHostedField from 'atoms/RecurlyHostedField';
    import { mapState } from 'vuex';
    import { tracking } from '../services/Tracking';

    export default {
        name: 'FormFields',
        components: {
            RecurlyHostedField,
            CustomSelect,
            InputElm,
            LabelInput,
            LabelInputBirthDate,
            CheckboxElm,
            LabelInputSheerID,
        },

        props: {
            formFieldList: {
                type: Object,
                required: true,
            },

            fieldErrors: {
                type: Object,
                required: true,
            },

            itemWrapperClass: {
                type: Function,
                required: false,
                default: (fieldConfig) => {
                    let classList = ['grid-item'];

                    if (fieldConfig && typeof fieldConfig === 'object') {
                        if (typeof fieldConfig.name === 'string') {
                            classList.push(`grid-item--${fieldConfig.name}`);
                        }

                        if (fieldConfig.type === 'hidden') {
                            classList.push(`grid-item--hidden`);
                        }
                    }

                    return classList;
                },
            },

            isFTC: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes,
            }),
            formFields() {
                let formFields = {};
                for (let key in this.formFieldList) {
                    if (!this.formFieldList.hasOwnProperty(key)) continue;

                    let config = this.formFieldList[key];
                    let { name, type } = config;
                    formFields[key] = {
                        name,
                        type,
                        config,
                        wrapperClass: this.itemWrapperClass(config),
                        errorMessage: this.fieldErrors[name],
                    };
                }

                return formFields;
            },
            trackingService() {
                return this.$trackingService ?? tracking;
            },
        },
        methods: {
            // START AB Test PLOCTOPLUS-2033 - Green lock icon on payment
            handleFocus() {
                this.$emit('recurlyFocus');
            },
            shouldDisplayHyperlinkText(field) {
                return (
                    field.name === 'ccNumber' &&
                    this.$store.getters['variant/isPaymentHypertextLink']
                );
            },
            shouldDisplayGreenLockIcon(field) {
                return (
                    field.name === 'ccNumber' &&
                    this.$store.getters['variant/isPaymentGreenLockIcon']
                );
            },
            executeLockIconTracking() {
                this.trackingService.trackAction('trackPaymentLearnMoreButtonSelect');
            },
            // END AB Test PLOCTOPLUS-2033 - Green lock icon on payment
            relayShowHidePasswordStatus(payload) {
                this.$emit('showHidePasswordStatus', payload);
            },
            handleInput(e, name) {
                this.updateFieldValue(name, e.value);
                this.validateField('input', name);
            },

            handleTrackCheckBox(e, name) {
                this.$emit('trackCheckBox', e.value, name);
            },

            handleSheerIDInput(e) {
                this.handleInput(e, e.name);
            },

            handleBlur(e, name) {
                // check if event has delayed payload
                if (e && typeof e === 'object' && 'value' in e) {
                    this.updateFieldValue(name, e.value);
                }

                this.validateField('blur', name);
            },

            handleKeyUp(e) {
                if (e.event.keyCode === 13) {
                    this.$emit('validateForm', { on: 'submit' });
                }
            },

            updateFieldValue(name, val) {
                let value = typeof val === 'string' ? val.trim() : val;

                this.$emit('updateFormData', { [name]: value });
            },

            validateField(on, name) {
                this.$emit('validateField', { name, on });
            },
        },
    };
</script>
